import { GroupMessage } from "../types";
import { RealtimeAdapter } from "./types";

export class CouchDBAdapter implements RealtimeAdapter {
  private channelId: string | null = null;
  private messageCallback: ((message: GroupMessage) => void) | null = null;
  private eventSource: EventSource | null = null;
  private isSubscribed = false;
  private lastSequence: string | null = null;

  constructor(private baseUrl: string = "/api/v3/customer/group-orders") {}

  async createChannel(channelId: string): Promise<void> {
    this.channelId = channelId;

    // Create the CouchDB database via our API
    const response = await fetch(`${this.baseUrl}/channel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ channelId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create channel: ${response.statusText}`);
    }
  }

  async subscribeToMessages(
    callback: (message: GroupMessage) => void,
  ): Promise<void> {
    if (!this.channelId) {
      throw new Error("Channel not created. Call createChannel first.");
    }

    this.messageCallback = callback;
    this.isSubscribed = true;

    // Start listening to changes stream
    await this.startChangesStream();
  }

  private async startChangesStream(): Promise<void> {
    if (!this.channelId || !this.isSubscribed) return;

    const params = new URLSearchParams({
      feed: "eventsource",
      heartbeat: "10000",
      include_docs: "true",
      ...(this.lastSequence && { since: this.lastSequence }),
    });

    const dbName = `${import.meta.env.VITE_ENV}/group-order/${this.channelId}`;
    const encodedDbName = encodeURIComponent(dbName);

    const url = `https://couchdb-realtime-production.hq.thelemmon.ch/${encodedDbName}/_changes?${params.toString()}`;

    this.eventSource = new EventSource(url);

    this.eventSource.onmessage = (event) => {
      try {
        console.log(event, event.data);
        const change = JSON.parse(event.data);
        console.log("message change", change);

        // Skip heartbeat messages
        if (!change.doc || change.deleted) return;

        // Update last sequence
        if (change.seq) {
          this.lastSequence = change.seq;
        }
        // Extract the group message from the document
        if (change.doc.type === "group-message" && change.doc.data) {
          const groupMessage = change.doc.data as GroupMessage;
          if (this.messageCallback) {
            this.messageCallback(groupMessage);
          }
        }
      } catch (error) {
        console.error("Error processing change:", error);
      }
    };

    this.eventSource.onerror = (error) => {
      console.error("EventSource error:", error);

      // Attempt to reconnect after a delay
      if (this.isSubscribed) {
        setTimeout(() => {
          if (this.isSubscribed) {
            this.startChangesStream();
          }
        }, 2000);
      }
    };
  }

  async readHistory(
    bookmark?: any,
  ): Promise<{ messages: GroupMessage[]; bookmark?: any }> {
    if (!this.channelId) {
      throw new Error("Channel not created. Call createChannel first.");
    }

    // Fetch all documents from the CouchDB database
    const params = new URLSearchParams({
      include_docs: "true",
      limit: "500",
      ...(bookmark && { since: `${bookmark}` }),
    });

    const response = await fetch(
      `${this.baseUrl}/channel/${this.channelId}/changes?${params}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to read history: ${response.statusText}`);
    }

    const data: any = await response.json();
    const messages: GroupMessage[] = [];

    // Extract messages from the response
    if (data.results) {
      for (const row of data.results) {
        if (row.doc && row.doc.type === "group-message" && row.doc.data) {
          const groupMessage = row.doc.data as GroupMessage;
          messages.push(groupMessage);
        }
      }
    }

    // Sort messages by timestamp (oldest first)
    messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

    return {
      messages,
      bookmark: data.last_seq,
    };
  }

  async sendMessage(message: GroupMessage): Promise<void> {
    if (!this.channelId) {
      throw new Error("Channel not created. Call createChannel first.");
    }

    // Add timestamp if not present
    if (!message.timestamp) {
      message.timestamp = Date.now();
    }

    // Create a document for this message
    const doc = {
      _id: `message_${message.timestamp}_${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      type: "group-message",
      data: message,
      created_at: new Date().toISOString(),
    };

    const response = await fetch(
      `${this.baseUrl}/channel/${this.channelId}/message`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(doc),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to send message: ${response.statusText}`);
    }
  }

  async unsubscribe(): Promise<void> {
    this.isSubscribed = false;
    this.messageCallback = null;

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  async close(): Promise<void> {
    await this.unsubscribe();
    this.channelId = null;
    this.lastSequence = null;
  }
}
